import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { ProjectProgressCard } from '../components/ProjectProgressCard';
import { ScheduleHealthCard } from '../components/ScheduleHealthCard';
import { ActivityFeedCard } from '../components/ActivityFeedCard';
import { TeamWorkloadCard } from '../components/TeamWorkloadCard';
import { DiscussionHotspotsCard } from '../components/DiscussionHotspotsCard';
import * as projectService from '../services/projectService';
import * as requirementService from '../services/requirementService';
import type { Project, ProjectMember } from '../services/projectService';
import type { RequirementWithDetails } from '../services/requirementService';

export function ProjectMetrics(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [project, setProject] = useState<Project | null>(null);
  const [requirements, setRequirements] = useState<RequirementWithDetails[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      if (!token || !id) return;

      setLoading(true);
      setError('');

      try {
        // Load project details
        const projectData = await projectService.getProjectById(id, token);
        setProject(projectData.project);
        setMembers(projectData.members || []);

        // Load requirements
        const requirementsData = await requirementService.getProjectRequirements(id, token);
        setRequirements(requirementsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load project metrics');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, token]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-gray-500 dark:text-gray-400">Loading project metrics...</div>
        </div>
      </Layout>
    );
  }

  if (error || !project) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-red-600 dark:text-red-400">{error || 'Project not found'}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg mb-6 p-6">
          <div className="flex justify-between items-center">
            <div>
              <button
                onClick={() => navigate(`/projects/${id}`)}
                className="text-sm text-blue-100 hover:text-white mb-2 inline-flex items-center transition-colors"
              >
                ← Back to Project
              </button>
              <h1 className="text-3xl font-bold text-white">
                Project Metrics
              </h1>
              <p className="text-blue-100 mt-1">{project.name}</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="text-sm text-blue-100">Project Code</div>
                <div className="text-lg font-semibold text-white">{project.project_code}</div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-md bg-red-50 border border-red-200">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 space-y-6">
            {/* Progress Tracking */}
            <ProjectProgressCard requirements={requirements} />

            {/* Schedule Health */}
            <ScheduleHealthCard project={project} requirements={requirements} />

            {/* Discussion Hotspots */}
            <DiscussionHotspotsCard requirements={requirements} projectId={id!} />
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="space-y-6">
            {/* Activity Feed */}
            <ActivityFeedCard projectId={id!} />

            {/* Team Workload */}
            <TeamWorkloadCard requirements={requirements} members={members} />
          </div>
        </div>
      </div>
    </Layout>
  );
}
